import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Search, X, Check, ChevronDown } from 'lucide-react';

const JobHeaderLocationFilter = ({ selectedCities = [], setSelectedCities = () => {}, cityOptions = [] }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = cityOptions.filter(o =>
        o.label.toLowerCase().includes(search.toLowerCase())
    );

    const toggle = (option) => {
        setSelectedCities(prev => {
            const exists = prev.some(c => c.value === option.value);
            return exists ? prev.filter(c => c.value !== option.value) : [...prev, option];
        });
    };

    const triggerLabel = selectedCities.length === 0
        ? 'Select location'
        : selectedCities.length === 1
            ? selectedCities[0].label
            : `${selectedCities.length} locations selected`;

    return (
        <div ref={ref} className="relative">
            {/* Trigger button */}
            <button
                type="button"
                onClick={() => setIsOpen(v => !v)}
                className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                    selectedCities.length > 0
                        ? 'bg-[#EEF1FE] border-[#3F57E0] text-[#3F57E0]'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
            >
                <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 shrink-0" />
                    <span className="truncate">{triggerLabel}</span>
                </div>
                <ChevronDown className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Selected chips (shown below trigger when closed) */}
            {selectedCities.length > 0 && !isOpen && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                    {selectedCities.map(city => (
                        <span key={city.value} className="flex items-center gap-1 px-2.5 py-1 bg-[#EEF1FE] text-[#3F57E0] text-[12px] font-medium rounded-full">
                            {city.label}
                            <button type="button" onClick={() => toggle(city)} className="hover:text-[#3347c7]">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {/* Dropdown panel */}
            {isOpen && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl border border-gray-200 shadow-xl z-[200] overflow-hidden">
                    {/* Search */}
                    <div className="p-2 border-b border-gray-100">
                        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg focus-within:ring-1 focus-within:ring-[#3F57E0] focus-within:border-[#3F57E0] border border-transparent transition-all">
                            <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <input
                                type="text"
                                placeholder="Search city or region…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                autoFocus
                                className="flex-1 text-sm bg-transparent outline-none text-gray-700 placeholder-gray-400"
                            />
                            {search && (
                                <button type="button" onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Options */}
                    <div className="max-h-52 overflow-y-auto">
                        {filtered.length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-5">No locations found</p>
                        ) : filtered.map(opt => {
                            const active = selectedCities.some(c => c.value === opt.value);
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => toggle(opt)}
                                    className={`flex items-center justify-between w-full px-4 py-2.5 text-sm text-left transition-colors ${
                                        active
                                            ? 'bg-[#EEF1FE] text-[#3F57E0] font-medium'
                                            : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                >
                                    <span>{opt.label}</span>
                                    {active && <Check className="w-3.5 h-3.5 shrink-0" />}
                                </button>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    {selectedCities.length > 0 && (
                        <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 bg-gray-50">
                            <span className="text-xs text-gray-500">{selectedCities.length} selected</span>
                            <button
                                type="button"
                                onClick={() => setSelectedCities([])}
                                className="text-xs font-medium text-[#3F57E0] hover:underline"
                            >
                                Clear
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default JobHeaderLocationFilter;
